import { useEffect, useMemo, useRef, useState } from 'react';
import { 
  Plus, Search, List, LayoutGrid, GripVertical, 
  Edit3, Trash2, Eye, EyeOff, Save, X, 
  CheckCircle2, AlertCircle, Image as ImageIcon, Info, 
  Camera, FileText, Settings, ChevronRight, RefreshCcw, 
  Play, ExternalLink, Monitor, Type
} from 'lucide-react';
import { getFileUrl, pb } from '../../lib/pb';
import { fetchHomeSlidesAdmin, createHomeSlide, updateHomeSlide, deleteHomeSlide } from '../../lib/homeSlides';
import '../../styles/admin.css';

export default function AdminHomeSlides() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [mode, setMode] = useState('create');
  const [viewMode, setViewMode] = useState('grid');
  const [filterStatus, setFilterStatus] = useState('all');
  const [draggingId, setDraggingId] = useState(null);
  const [success, setSuccess] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchHomeSlidesAdmin(page, 50, search);
      setItems(res.items || []);
      setTotalPages(res.totalPages || 1);
    } catch (e) {
      setError(e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [page, search]);

  useEffect(() => {
    let unsub = null;
    (async () => {
      try {
        unsub = await pb.collection('home_slides').subscribe('*', () => load());
      } catch (e) { console.error(e); }
    })();
    return () => { if (unsub) pb.collection('home_slides').unsubscribe('*'); };
  }, []);

  const filteredItems = useMemo(() => {
    return items.filter(m => {
      if (filterStatus === 'published' && !m.published) return false;
      if (filterStatus === 'draft' && m.published) return false;
      return true;
    });
  }, [items, filterStatus]);

  const handleDragStart = (id) => setDraggingId(id);

  const handleDragOver = (targetId, e) => {
    e.preventDefault();
    if (!draggingId || draggingId === targetId) return;
    setItems(prev => {
      const next = [...prev];
      const fromIdx = next.findIndex(s => s.id === draggingId);
      const toIdx = next.findIndex(s => s.id === targetId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  };

  const persistOrder = async () => {
    if (!items.length) return;
    try {
      const updates = items.map((s, index) => {
        const newOrder = (index + 1) * 10;
        if (s.order !== newOrder) return updateHomeSlide(s.id, { order: newOrder });
        return null;
      }).filter(Boolean);
      if (updates.length) {
        await Promise.all(updates);
        setSuccess('Storyboard mis à jour ✨');
        setTimeout(() => setSuccess(''), 2000);
      }
    } catch (e) { setError("Erreur de réorganisation"); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    persistOrder();
    setDraggingId(null);
  };

  const handleTogglePublished = async (m) => {
    // Mise à jour optimiste immédiate
    const oldStatus = m.published;
    setItems(prev => prev.map(s => s.id === m.id ? { ...s, published: !oldStatus } : s));

    try {
      await updateHomeSlide(m.id, { published: !oldStatus });
      setSuccess(!oldStatus ? 'Slide activé 🌕' : 'Slide masqué 🌒');
      setTimeout(() => setSuccess(''), 2000);
    } catch (e) { 
      // Rollback en cas d'erreur
      setItems(prev => prev.map(s => s.id === m.id ? { ...s, published: oldStatus } : s));
      setError(e.message); 
    }
  };

  const openCreate = () => {
    setMode('create');
    setEditing({ title: '', subtitle: '', cta_label: '', cta_href: '', image: '', published: true });
  };

  const openEdit = (m) => { setMode('edit'); setEditing(m); };

  const handleDelete = async (m) => {
    if (!window.confirm(`Supprimer le slide "${m.title || 'Sans titre'}" ?`)) return;
    try {
      await deleteHomeSlide(m.id);
      setSuccess('Slide supprimé 🗑️');
      setTimeout(() => setSuccess(''), 2000);
    } catch (e) { setError(e.message); }
  };

  return (
    <div className="admin-main animation-fade-in">
      <header className="admin-header-premium">
        <div className="header-content">
          <div className="header-tag">Identité Visuelle</div>
          <h1>Bandeaux d'Accueil <span className="count-badge">{items.length}</span></h1>
          <p className="header-subtitle">Pilotez le carrousel principal et personnalisez vos messages de bienvenue.</p>
        </div>
        <button className="btn-add-premium" onClick={openCreate}>
          <Plus size={20} />
          <span>Nouveau Slide</span>
        </button>
      </header>

      {success && <div className="toast-success"><CheckCircle2 size={18}/> {success}</div>}
      {error && <div className="toast-error"><AlertCircle size={18}/> {error}</div>}

      <div className="admin-controls-bar">
        <div className="search-box-premium">
          <Search size={18} className="search-icon" />
          <input 
            placeholder="Rechercher par titre ou slogan..." 
            value={search} 
            onChange={e => { setPage(1); setSearch(e.target.value); }}
          />
        </div>

        <div className="filters-and-view">
          <div className="capsule-select">
            <button className={filterStatus === 'all' ? 'active' : ''} onClick={() => setFilterStatus('all')}>Tous</button>
            <button className={filterStatus === 'published' ? 'active' : ''} onClick={() => setFilterStatus('published')}>Actifs</button>
            <button className={filterStatus === 'draft' ? 'active' : ''} onClick={() => setFilterStatus('draft')}>Archives</button>
          </div>

          <div className="view-switcher-premium">
            <button className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')}><List size={18}/></button>
            <button className={viewMode === 'grid' ? 'active' : ''} onClick={() => setViewMode('grid')}><LayoutGrid size={18}/></button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="loading-state">
          <RefreshCcw className="spin" size={32} />
          <p>Mise en place du projecteur...</p>
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="empty-state-card">
          <Monitor size={48} />
          <h3>Votre écran est vide</h3>
          <p>Ajoutez un premier slide pour donner vie à votre page d'accueil.</p>
          <button className="btn filled" onClick={openCreate}>Créer un slide</button>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className={`slides-content-container ${viewMode}`}>
          {viewMode === 'list' ? (
            <div className="premium-list">
              {filteredItems.map((m) => (
                <div 
                  key={m.id} 
                  className={`list-item-row ${draggingId === m.id ? 'dragging' : ''}`}
                  draggable
                  onDragStart={() => handleDragStart(m.id)}
                  onDragOver={(e) => handleDragOver(m.id, e)}
                  onDrop={handleDrop}
                >
                  <div className="drag-handle"><GripVertical size={18}/></div>
                  <div className="item-icon-box cinematic">
                    {m.image ? (
                      <img src={getFileUrl(m, m.image)} alt={m.title} />
                    ) : (
                      <ImageIcon size={20} className="placeholder" />
                    )}
                  </div>
                  <div className="item-info">
                    <div className="item-title">{m.title || 'Sans titre'}</div>
                    <div className="item-meta">
                      {m.cta_label ? `Bouton: ${m.cta_label}` : 'Sans bouton'} • {m.subtitle || 'Pas de sous-titre'}
                    </div>
                  </div>
                  <div className="item-status">
                     <button className={`status-pill ${m.published ? 'online' : 'offline'}`} onClick={() => handleTogglePublished(m)}>
                        {m.published ? <Eye size={14}/> : <EyeOff size={14}/>}
                        <span>{m.published ? 'Actif' : 'Archivé'}</span>
                     </button>
                  </div>
                  <div className="item-actions">
                    <button className="action-btn edit" onClick={() => openEdit(m)} title="Modifier">
                      <Edit3 size={18}/>
                    </button>
                    <button className="action-btn delete" onClick={() => handleDelete(m)} title="Supprimer">
                      <Trash2 size={18}/>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="premium-grid-slides">
              {filteredItems.map((m) => (
                <div 
                  key={m.id} 
                  className="slide-card-premium animation-slide-up"
                  draggable
                  onDragStart={() => handleDragStart(m.id)}
                  onDragOver={(e) => handleDragOver(m.id, e)}
                  onDrop={handleDrop}
                >
                  <div className="card-media cinematic-box">
                    {m.image ? <img src={getFileUrl(m, m.image)} alt={m.title} /> : <div className="card-placeholder"><ImageIcon size={40}/></div>}
                    <div className="card-media-overlay">
                       <div className={`status-badge-premium ${m.published ? 'active' : ''}`}>{m.published ? 'En ligne' : 'Archivé'}</div>
                    </div>
                  </div>
                  <div className="card-body">
                    <h3 className="card-title">{m.title || 'Sans titre'}</h3>
                    <p className="card-subtitle-mini">{m.subtitle}</p>
                    <div className="card-actions-row">
                       <div className="cta-hint">{m.cta_label || 'Pas de CTA'}</div>
                       <div className="spacer"></div>
                       <button className={`btn-mini ${m.published ? 'online' : 'offline'}`} onClick={() => handleTogglePublished(m)} title={m.published ? 'Masquer' : 'Publier'}>
                          {m.published ? <Eye size={16}/> : <EyeOff size={16}/>}
                       </button>
                       <button className="btn-mini edit" onClick={() => openEdit(m)} title="Modifier"><Edit3 size={16}/></button>
                       <button className="btn-mini delete" onClick={() => handleDelete(m)} title="Supprimer"><Trash2 size={16}/></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {editing && (
        <SlideStudio
          mode={mode}
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      <style>{`
        .premium-grid-slides { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 25px; padding: 20px 0; }
        
        .slide-card-premium { background: #fff; border-radius: 28px; border: 1px solid var(--admin-border); overflow: hidden; position: relative; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); cursor: grab; }
        .slide-card-premium:hover { transform: translateY(-8px); border-color: var(--admin-primary); box-shadow: 0 20px 40px -15px rgba(0,0,0,0.1); }
        
        .cinematic-box { height: 180px; position: relative; background: #0f172a; overflow: hidden; }
        .cinematic-box img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.6s ease; }
        .slide-card-premium:hover .cinematic-box img { transform: scale(1.1); }
        
        .card-media-overlay { position: absolute; top: 15px; left: 15px; right: 15px; display: flex; justify-content: flex-end; }
        .status-badge-premium { font-size: 10px; font-weight: 800; text-transform: uppercase; background: rgba(0,0,0,0.4); backdrop-filter: blur(8px); color: #fff; padding: 4px 12px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.1); }
        .status-badge-premium.active { background: rgba(16, 185, 129, 0.8); border-color: rgba(16, 185, 129, 0.4); }

        .slide-card-premium .card-body { padding: 20px; }
        .slide-card-premium .card-title { font-size: 17px; font-weight: 900; color: #0f172a; margin: 0 0 5px 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .card-subtitle-mini { font-size: 13px; color: #64748b; line-height: 1.4; height: 36px; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; margin-bottom: 15px; }

        .card-actions-row { display: flex; align-items: center; gap: 8px; border-top: 1px solid #f8fafc; padding-top: 15px; }
        .cta-hint { font-size: 10px; font-weight: 800; text-transform: uppercase; color: var(--admin-primary); background: var(--admin-primary-soft); padding: 2px 8px; border-radius: 6px; }
        .spacer { flex: 1; }

        .item-icon-box.cinematic { width: 100px; height: 60px; background: #0f172a; }
        .item-icon-box.cinematic img { width: 100%; height: 100%; object-fit: cover; }

        @media (max-width: 600px) {
          .premium-grid-slides { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
}

function SlideStudio({ mode, initial, onClose, onSaved }) {
  const [activeTab, setActiveTab] = useState('content');
  const [title, setTitle] = useState(initial.title || '');
  const [subtitle, setSubtitle] = useState(initial.subtitle || '');
  const [ctaLabel, setCtaLabel] = useState(initial.cta_label || '');
  const [ctaHref, setCtaHref] = useState(initial.cta_href || '');
  const [published, setPublished] = useState(initial.published ?? true);
  const [image, setImage] = useState(() => initial.image ? { type: 'existing', name: initial.image } : null);
  
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const compressImage = (file, maxWidth = 1920, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        try {
          let { width, height } = img;
          if (width > maxWidth) { height = (height * maxWidth) / width; width = maxWidth; }
          canvas.width = width; canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('Compression failed')), 'image/jpeg', quality);
        } catch (err) { reject(err); }
      };
      img.onerror = () => reject(new Error("Image error"));
      img.src = URL.createObjectURL(file);
    });
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await compressImage(file);
      const finalFile = new File([compressed], file.name, { type: 'image/jpeg' });
      setImage({ type: 'new', file: finalFile });
    } catch (err) { setError("Erreur compression image"); }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const data = { title, subtitle, cta_label: ctaLabel, cta_href: ctaHref, published };
    if (image) { if (image.type === 'new') data.image = image.file; } 
    else { data.image = null; }

    try {
      if (mode === 'create') await createHomeSlide(data);
      else await updateHomeSlide(initial.id, data);
      onSaved();
    } catch (err) { setError(err?.message || 'Erreur sauvegarde'); } 
    finally { setSaving(false); }
  };

  const previewUrl = image ? (image.type === 'existing' ? getFileUrl(initial, image.name) : URL.createObjectURL(image.file)) : '';

  return (
    <div className="admin-modal-overlay">
      <div className="admin-modal-container studio-slide animation-slide-up">
        <header className="modal-header-premium">
          <div className="header-info">
            <div className="header-tag">{mode === 'create' ? 'Metteur en scène' : 'Montage'}</div>
            <h2>Studio Slide Accueil</h2>
          </div>
          <button className="btn-close-modal" onClick={onClose}><X size={20}/></button>
        </header>

        <div className="modal-tabs">
          <button className={activeTab === 'content' ? 'active' : ''} onClick={() => setActiveTab('content')}>
            <Type size={16}/> <span>Contenu</span>
          </button>
          <button className={activeTab === 'visual' ? 'active' : ''} onClick={() => setActiveTab('visual')}>
            <Camera size={16}/> <span>Visuel Panorama</span>
          </button>
        </div>

        <form onSubmit={onSubmit} className="modal-body-premium">
           {error && <div className="modal-error"><AlertCircle size={14}/> {error}</div>}

           <div className="tab-panels">
              {activeTab === 'content' && (
                <div className="tab-pane active animation-fade-in">
                   <div className="form-group-premium">
                      <label>Slogan principal (H1/H2)</label>
                      <input value={title} onChange={e => setTitle(e.target.value)} required placeholder="ex: Expertise Solaire & Rénovation RGE" />
                   </div>
                   <div className="form-group-premium">
                      <label>Message de description</label>
                      <textarea rows={3} value={subtitle} onChange={e => setSubtitle(e.target.value)} placeholder="Quelques mots pour convaincre dès le premier regard..." />
                   </div>
                   <div className="form-row-premium" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                      <div className="form-group-premium">
                        <label>Texte du bouton (CTA)</label>
                        <input value={ctaLabel} onChange={e => setCtaLabel(e.target.value)} placeholder="ex: Demander un devis" />
                      </div>
                      <div className="form-group-premium">
                        <label>Lien de destination</label>
                        <input value={ctaHref} onChange={e => setCtaHref(e.target.value)} placeholder="ex: /contact ou /realisations" />
                      </div>
                   </div>
                   <div className="toggle-switch-group">
                      <span>Diffuser ce slide dans le carrousel</span>
                      <label className="switch">
                         <input type="checkbox" checked={published} onChange={e => setPublished(e.target.checked)} />
                         <span className="slider round"></span>
                      </label>
                   </div>
                </div>
              )}

              {activeTab === 'visual' && (
                <div className="tab-pane active animation-fade-in">
                   <div className="visual-studio">
                      <div className="preview-main-box slide-panorama" style={{ width: '100%', height: 260, borderRadius: 24, background: '#0f172a' }}>
                         {previewUrl ? <img src={previewUrl} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div className="no-image"><Monitor size={64}/></div>}
                      </div>
                      <div className="visual-actions" style={{ marginTop: 20 }}>
                         <label className="btn-upload-premium" style={{ width: '100%', justifyContent: 'center' }}>
                            <Camera size={18}/> <span>Changer l'image HD</span>
                            <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                         </label>
                         <div className="visual-notes" style={{ display: 'flex', gap: 10, marginTop: 15, color: '#94a3b8', fontSize: 12 }}>
                            <Info size={16}/> 
                            <span>Format recommandé : 1920x1080px. Évitez les images trop chargées pour que le texte reste lisible.</span>
                         </div>
                      </div>
                   </div>
                </div>
              )}
           </div>

           <footer className="modal-footer-premium">
              <button type="button" className="btn-modal-cancel" onClick={onClose}>Annuler</button>
              <button type="submit" className="btn-modal-save" disabled={saving}>
                 {saving ? <RefreshCcw size={18} className="spin"/> : <Save size={18}/>}
                 <span>{mode === 'create' ? 'Ajouter au Storyboard' : 'Mettre au montage'}</span>
              </button>
           </footer>
        </form>
      </div>
    </div>
  );
}
